import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { MediaAsset, PlaylistItem } from '@ghostscreen/contracts';
import { canManageContent } from '../../lib/auth/permissions';
import { api } from '../../lib/api';
import { buildOverviewStatus } from '../../lib/dashboard/overview/buildOverviewStatus';
import { useQueryRetry } from '../../lib/dashboard/useQueryRetry';
import { useCurrentMembership } from '../../lib/organisation/useCurrentMembership';
import { useOrganisationId } from '../../lib/session';
import { useScreenAssignments } from '../overview/useScreenAssignments';
import { buildHomeDisplayRows, countConnectedDisplays } from './buildHomeDisplayRows';
import { buildHomeHero } from './buildHomeHero';
import { buildHomePlaylistCards, sortHomePlaylistCards } from './buildHomePlaylistCards';
import { buildHomeQuickActions } from './buildHomeQuickActions';

const RECENTLY_EDITED_LIMIT = 5;

function combinePlaylistItems(results: UseQueryResult<PlaylistItem[]>[]) {
  return {
    data: results.map((result) => result.data),
    isPending: results.some((result) => result.isPending),
    isError: results.some((result) => result.isError),
    refetchFailed: () =>
      Promise.all(results.filter((result) => result.isError).map((result) => result.refetch())),
  };
}

/**
 * Everything Home shows, derived from the organisation's live data. Query keys
 * are shared with Screens / Playlists / Content / Schedule / Designer so edits
 * there show up here without a reload.
 */
export function useHomeData() {
  const organisationId = useOrganisationId();
  const membership = useCurrentMembership();
  const { i18n } = useTranslation('home');
  const language = i18n.language;

  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
    refetchInterval: 15_000,
  });
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });
  const mediaQuery = useQuery({
    queryKey: ['media', organisationId],
    queryFn: () => api.listMediaAssets(organisationId),
  });
  const schedulesQuery = useQuery({
    queryKey: ['schedules', organisationId],
    queryFn: () => api.listSchedules(organisationId),
  });
  const designRequestsQuery = useQuery({
    queryKey: ['design-requests', organisationId],
    queryFn: () => api.listDesignRequests(organisationId),
  });

  const screens = useMemo(() => screensQuery.data ?? [], [screensQuery.data]);
  const playlists = useMemo(() => playlistsQuery.data ?? [], [playlistsQuery.data]);
  const { assignments, isPending: assignmentsPending } = useScreenAssignments(
    organisationId,
    screens,
  );

  const items = useQueries({
    queries: playlists.map((playlist) => ({
      queryKey: ['playlist-items', playlist.id],
      queryFn: () => api.listPlaylistItems(organisationId, playlist.id),
    })),
    combine: combinePlaylistItems,
  });

  const itemsByPlaylistId = useMemo(
    () => new Map(playlists.map((playlist, index) => [playlist.id, items.data[index] ?? []])),
    [playlists, items.data],
  );

  const mediaById = useMemo(
    () => new Map((mediaQuery.data ?? []).map((asset: MediaAsset) => [asset.id, asset])),
    [mediaQuery.data],
  );

  const coreQueries = [screensQuery, playlistsQuery, mediaQuery];
  const isError =
    coreQueries.some((query) => query.isError) || items.isError || membership.isError;
  const isPending =
    coreQueries.some((query) => query.isPending) ||
    items.isPending ||
    membership.isPending ||
    assignmentsPending;

  const { isRetrying, retry } = useQueryRetry(async () => {
    await Promise.all([
      ...coreQueries.filter((query) => query.isError).map((query) => query.refetch()),
      items.refetchFailed(),
    ]);
  });

  const canManage = canManageContent(membership.membership?.role);
  const schedules = schedulesQuery.isSuccess ? schedulesQuery.data : null;
  const designRequests = designRequestsQuery.isSuccess ? designRequestsQuery.data : null;

  const model = useMemo(() => {
    const cards = buildHomePlaylistCards({
      playlists,
      itemsByPlaylistId,
      mediaById,
      screens,
      assignments,
    });
    const status = buildOverviewStatus(screens, assignments);
    const displayRows = buildHomeDisplayRows(screens, assignments, playlists);

    return {
      cards,
      recent: sortHomePlaylistCards(cards, 'edited').slice(0, RECENTLY_EDITED_LIMIT),
      playingCount: cards.filter((card) => card.playing).length,
      hero: buildHomeHero({ screens, assignments, cards, status, canManage }),
      quickActions: buildHomeQuickActions({
        cards,
        screens,
        designRequests,
        schedules,
        canManage,
      }),
      issues: status.issues,
      hasScreens: screens.length > 0,
      displayRows,
      onlineCount: countConnectedDisplays(displayRows),
      canManage,
    };
  }, [
    // The builders return translated labels, so rebuild when the language changes.
    language,
    playlists,
    itemsByPlaylistId,
    mediaById,
    screens,
    assignments,
    designRequests,
    schedules,
    canManage,
  ]);

  return { model, isPending, isError, isRetrying, retry };
}

export type HomeModel = ReturnType<typeof useHomeData>['model'];
